import * as THREE from "three";
import type { VFX } from "../../../../../art/vfx.js";
import { Cell, Grid } from "@highmarch/domain";
import { WornSkins } from "../skins/wornSkins";
import { lib } from "./common";
import { critterGround } from "./critters";
import { HubCritters } from "./critterHub";
import { PetBody } from "./petBody";
import { PET_MOVE, turnToward } from "./petGait";
import { PetHop } from "./petHop";
import { PET_CLIP } from "./petMind";
import { PetMarker } from "./petMarker";
import { petLook } from "./petPresent";

/** The pet's bounds for the anchor (world units at its scale 1): half its footprint and its height. */
const HUB_PET_BOX = { half: 0.18, height: 0.34 } as const;
const _lo = new THREE.Vector3(), _hi = new THREE.Vector3();

/** What the hub pet does next, and for how long (seconds; a wander lasts as long as its hop). */
export type HubPetDoing = { readonly kind: "idle"; readonly seconds: number; readonly fidget: boolean } | { readonly kind: "sleep"; readonly seconds: number } | { readonly kind: "wander" };

export const HUB_PET = {
  /** Chances of each next activity (the rest is idling). */
  sleepShare: 0.2, wanderShare: 0.4,
  /** How long it idles (it may fidget once in it) and naps, seconds (uniform in the range). */
  idle: [3, 7] as const, sleep: [8, 14] as const,
  /** Share of idles with a fidget in them. */
  fidgetShare: 0.5,
  /** It keeps to lawn cells this near the fortress (cells, the larger of the two axes) that the hub's shot shows… */
  near: 3,
  /**
   * …no further back than the keep's own centre line: a cell offset (dx, dz) stands (dx + dz)·0.71 cells behind the
   * fortress on screen (HubLayout.Stands), and behind it the keep's roofs hide the pet and the hub's haze swallows it.
   */
  maxDepth: 0,
  /** Jitter of its spot inside a cell (cells). */
  jitter: 0.4,
  /** With several pets on the lawn it reaches this many cells further out, until it has this many cells a pet. */
  wider: 3, cellsPerPet: 2,
} as const;

/** Picks the hub pet's next activity from one draw in [0, 1) and a second for its length. Pure. */
export function nextHubPet(r: number, r2: number): HubPetDoing {
  const span = (range: readonly [number, number]) => range[0] + r2 * (range[1] - range[0]);
  if (r < HUB_PET.sleepShare) return { kind: "sleep", seconds: span(HUB_PET.sleep) };
  if (r < HUB_PET.sleepShare + HUB_PET.wanderShare) return { kind: "wander" };
  return { kind: "idle", seconds: span(HUB_PET.idle), fidget: r2 < HUB_PET.fidgetShare };
}

/** The lawn cells the hub pet may stand on: the critters' lawn (HubCritters.lawn) near the fortress, in front of the keep. */
export function hubPetCells(lawn: readonly Cell[], base: Cell, near: number = HUB_PET.near): Cell[] {
  const close = lawn.filter((c) => Math.max(Math.abs(c.x - base.x), Math.abs(c.y - base.y)) <= near);
  const front = close.filter((c) => c.x - base.x + (c.y - base.y) <= HUB_PET.maxDepth);
  return front.length > 1 ? front : close;
}

/** The lawn for `pets` pets: hubPetCells, reaching further from the keep (HUB_PET.wider) until each has room. Pure. */
export function hubPetLawn(lawn: readonly Cell[], base: Cell, pets: number): Cell[] {
  let cells = hubPetCells(lawn, base);
  for (let near = HUB_PET.near + 1; near <= HUB_PET.near + HUB_PET.wider && cells.length < pets * HUB_PET.cellsPerPet; near++) cells = hubPetCells(lawn, base, near);
  return cells;
}

/**
 * The cells of `cells` the shot shows (`inShot`: a ground point's x / z, true when it lands in the framed band), all of
 * them when the shot shows none (a frame not measured yet). Pure.
 */
export function cellsInShot(cells: readonly Cell[], inShot: (x: number, z: number) => boolean): Cell[] {
  const seen = cells.filter((c) => inShot(c.x, c.y));
  return seen.length > 0 ? seen : [...cells];
}

/**
 * Whether a tower on one of `towers` stands between the camera and `c`: one cell off or less, and nearer the camera (the
 * hub camera looks along +x +z, so a smaller x + z is nearer; a tower's height covers the cells just behind it). Pure.
 */
export function behindATower(c: Cell, towers: readonly Cell[]): boolean {
  return towers.some((t) => Math.max(Math.abs(t.x - c.x), Math.abs(t.y - c.y)) <= 1 && t.x + t.y < c.x + c.y);
}

/** The cell to trot to from `from`: a neighbour, else one two cells off, else none. `pick` takes a draw in [0, 1). */
export function wanderTo(cells: readonly Cell[], from: Cell | null, pick: number): Cell | null {
  const within = (r: number) => cells.filter((c) => from === null || (!c.equals(from) && Math.max(Math.abs(c.x - from.x), Math.abs(c.y - from.y)) <= r));
  const near = within(1).length > 0 ? within(1) : within(2);
  return near.length > 0 ? near[Math.floor(pick * near.length)] : null;
}

/**
 * One pet on the hub's lawn (docs/PETS.md; Pets v3: every pet had wanders there, petHubPack.ts): in front of the
 * fortress it idles, fidgets, naps (`sleep`) and trots to a nearby lawn cell now and then. It shows while it is had
 * (WornSkins.petsOwned), wears its worn coat — rebuilt when either changes — and the one that comes along into battles
 * (WornSkins.pet) wears the gold ring. Presentation only.
 */
export class HubPet {
  /** The hub's sprite VFX for its coat's effects (hubFx.ts HubFx.ambient), or null. */
  fx: (() => VFX | null) | null = null;
  readonly group = new THREE.Group();
  /** All the critters' lawn, the part near the keep this pet keeps to, and the part of that the shot shows (`frame`). */
  private readonly allLawn: Cell[];
  private lawn: Cell[];
  private cells: Cell[];
  private framed: { inShot: (x: number, z: number) => boolean; towers: readonly Cell[] } | null = null;
  private body: PetBody | null = null;
  private dressedAs = "";
  private readonly hop = new PetHop();
  private cell: Cell | null = null;
  private doing: HubPetDoing = { kind: "idle", seconds: 0, fidget: false };
  private left = 0;
  private fidgetIn = -1;
  private act: "fidget" | "cheer" | "" = "";
  private actT = 0;
  private time = 0;
  private yaw = 0;

  /** The gold ring of the pet that comes along (null on the others). */
  private ring: PetMarker | null = null;

  /** `taken(c)`: another pet stands on cell `c` (they spread out over the lawn). */
  constructor(grid: Grid, private readonly base: Cell, stands: readonly Cell[], readonly petId: string, private readonly taken: (c: Cell, self: HubPet) => boolean = () => false,
              private readonly rand: () => number = Math.random) {
    this.allLawn = HubCritters.lawn(grid, base, stands);
    this.lawn = hubPetCells(this.allLawn, base);
    this.cells = this.lawn;
  }

  /** `pets` pets share the lawn (HubPets): it reaches further out so each has room (hubPetLawn). */
  room(pets: number): void {
    this.lawn = hubPetLawn(this.allLawn, this.base, pets);
    if (this.framed) this.frame(this.framed.inShot, this.framed.towers); else this.cells = this.lawn;
  }

  /**
   * The hub framed its shot again: the pet keeps to the cells it shows (`inShot`: a ground point's x / z), and off those
   * a tower on `towers` (the stands standing, the ghost's) hides.
   */
  frame(inShot: (x: number, z: number) => boolean, towers: readonly Cell[] = []): void {
    this.framed = { inShot, towers };
    const open = this.lawn.filter((c) => !behindATower(c, towers));
    this.cells = cellsInShot(open.length > 1 ? open : this.lawn, inShot);
    // standing somewhere the shot no longer shows: the next activity is a trot back into it
    if (this.cell && !this.cells.some((c) => c.equals(this.cell!))) this.left = 0;
  }

  update(dt: number): void {
    this.time += dt;
    const pet = WornSkins.petsOwned().includes(this.petId) ? this.petId : null;
    if (!this.dress(pet) || !this.body) { this.wearRing(false, dt); return; }
    const b = this.body;
    this.hop.step(dt);
    if (this.doing.kind === "wander" ? !this.hop.moving : (this.left -= dt) <= 0) this.choose();
    if (this.fidgetIn >= 0 && (this.fidgetIn -= dt) < 0 && !this.hop.moving) { b.play("fidget"); this.act = "fidget"; this.actT = 0; }
    this.actT += dt;
    if (this.act !== "" && this.actT >= PET_CLIP[this.act]) this.act = "";
    if (this.hop.moving && this.act !== "") { b.endAct(); this.act = ""; }
    if (this.hop.moving) this.yaw = turnToward(this.yaw, this.hop.heading, PET_MOVE.turnRate, dt);
    const gait = this.hop.gait;
    const loop = gait && this.hop.setOff && this.hop.left > PET_MOVE.fade / 2 ? gait : this.doing.kind === "sleep" ? "sleep" : "idle";
    b.root.position.set(this.hop.x, 0, this.hop.z);
    b.pose(dt, { loop, rate: this.hop.rate, yaw: this.yaw, ground: critterGround(this.hop.x, this.hop.z), act: this.act || loop, actT: this.act ? this.actT : this.time });
    this.wearRing(WornSkins.pet() === this.petId, dt);
  }

  /** The cell it stands on (or trots to), null before it is placed. */
  get at(): Cell | null { return this.cell; }

  /** The gold ring round the paws of the pet that comes along. */
  private wearRing(on: boolean, dt: number): void {
    if (on && !this.ring && this.body) { this.ring = new PetMarker(this.body.scale); this.group.add(this.ring.mesh); }
    if (!on && this.ring) { this.ring.dispose(); this.ring = null; }
    this.ring?.update(dt, this.hop.x, critterGround(this.hop.x, this.hop.z), this.hop.z);
  }

  /** Builds (or rebuilds, or hides) the body for the pet that comes along now; true when one is up. */
  private dress(pet: string | null): boolean {
    // the pet that comes along wears its full model; the others on the lawn their far LOD (SkinKits.petDrawn)
    const home = pet !== null && WornSkins.pet() !== pet;
    const key = pet === null ? "" : `${pet}|${WornSkins.petModel(pet)}|${WornSkins.of("pet", pet) ?? ""}|${lib()?.has(`hq:${WornSkins.petModel(pet)}`) ? 1 : 0}|${home ? 1 : 0}`;
    if (key === this.dressedAs) return this.body !== null;
    this.dressedAs = key;
    this.body?.dispose();
    this.body = null;
    if (pet === null || this.cells.length === 0) return false;
    this.body = new PetBody(pet, petLook().hubScale, undefined, home);
    this.body.fx = this.fx;
    this.hop.configure(this.body.gaits, this.body.scale);
    this.group.add(this.body.root, this.body.shadow);
    if (this.cell === null) {
      const free = this.cells.filter((c) => !this.taken(c, this));
      const from = free.length > 0 ? free : this.cells;
      this.cell = from[Math.floor(this.rand() * from.length)];
      const s = this.spotIn(this.cell);
      this.hop.place(s.x, s.z);
      this.yaw = this.rand() * Math.PI * 2;
    }
    return true;
  }

  /** The pet's bounds now (world space), for the hub's anchor over it; null while no pet is on the lawn. */
  box(out: THREE.Box3): THREE.Box3 | null {
    const b = this.body;
    if (!b) return null;
    const half = HUB_PET_BOX.half * b.scale, y = b.root.position.y;
    return out.set(_lo.set(this.hop.x - half, y, this.hop.z - half), _hi.set(this.hop.x + half, y + HUB_PET_BOX.height * b.scale, this.hop.z + half));
  }

  /** One-shot: its cheer (an upgrade finished), unless it is on the move. */
  cheer(): void {
    if (!this.body || this.hop.moving) return;
    this.body.play("cheer");
    this.act = "cheer";
    this.actT = 0;
  }

  private choose(): void {
    const shown = this.cell !== null && this.cells.some((c) => c.equals(this.cell!));
    this.doing = shown ? nextHubPet(this.rand(), this.rand()) : { kind: "wander" };     // out of the shot: back into it
    this.fidgetIn = -1;
    if (this.doing.kind === "wander") {
      const free = this.cells.filter((c) => !this.taken(c, this));
      const to = shown ? wanderTo(free, this.cell, this.rand()) : free[Math.floor(this.rand() * free.length)] ?? null;
      if (to === null) { this.doing = { kind: "idle", seconds: HUB_PET.idle[0], fidget: false }; this.left = this.doing.seconds; return; }
      this.cell = to;
      const s = this.spotIn(this.cell);
      this.hop.go(s.x, s.z, [], this.yaw);
      return;
    }
    this.left = this.doing.seconds;
    if (this.doing.kind === "idle" && this.doing.fidget) this.fidgetIn = this.rand() * Math.max(0, this.doing.seconds - PET_CLIP.fidget);
  }

  private spotIn(c: Cell): { x: number; z: number } {
    return { x: c.x + (this.rand() - 0.5) * HUB_PET.jitter, z: c.y + (this.rand() - 0.5) * HUB_PET.jitter };
  }

  dispose(): void {
    this.ring?.dispose();
    this.ring = null;
    this.body?.dispose();
    this.body = null;
    this.group.clear();
  }
}
